import type { ReactNode } from "react";
import enApprove from "./assets/sync-guide/en/approve.jpg";
import enConfirm from "./assets/sync-guide/en/confirm.jpg";
import enDevice from "./assets/sync-guide/en/device.jpg";
import enGoogle from "./assets/sync-guide/en/google.jpg";
import enQr from "./assets/sync-guide/en/qr.jpg";
import enReceive from "./assets/sync-guide/en/receive.jpg";
import zhApprove from "./assets/sync-guide/zh-HK/approve.jpg";
import zhConfirm from "./assets/sync-guide/zh-HK/confirm.jpg";
import zhDevice from "./assets/sync-guide/zh-HK/device.jpg";
import zhGoogle from "./assets/sync-guide/zh-HK/google.jpg";
import zhQr from "./assets/sync-guide/zh-HK/qr.jpg";
import zhReceive from "./assets/sync-guide/zh-HK/receive.jpg";
import BrandIcon from "./BrandIcon";
import { useTranslation } from "./i18n";
import { DRIVE_SYNC_RELEASED } from "./lib/features";
import "./sync-guide.css";

function Device({ tablet = false }: { tablet?: boolean }) {
  const { t } = useTranslation("marketing");
  return (
    <div className={`site-sync-device${tablet ? " site-sync-tablet" : ""}`}>
      <div className="site-sync-screen">
        <BrandIcon />
        <span>{t("syncSampleClass")}</span>
        <div className="site-sync-seats" aria-hidden="true">
          {Array.from({ length: 6 }, (_, index) => (
            <i key={index} />
          ))}
        </div>
        <small>✓ {t("syncSampleRecord")}</small>
      </div>
    </div>
  );
}

export function SyncDiagram() {
  const { t } = useTranslation("marketing");
  return (
    <figure className="site-sync-diagram">
      <div className="site-sync-route">
        <div>
          <Device />
          <b>{t("syncComputer")}</b>
        </div>
        <span className="site-sync-route-arrow" aria-hidden="true">
          ↔
        </span>
        <div className="site-sync-drive">
          <span aria-hidden="true">⇄</span>
          <b>Google Drive</b>
          <small>{t("syncEncrypted")}</small>
        </div>
        <span className="site-sync-route-arrow" aria-hidden="true">
          ↔
        </span>
        <div>
          <Device tablet />
          <b>{t("syncTablet")}</b>
        </div>
      </div>
      <figcaption>{t("syncDiagramCaption")}</figcaption>
    </figure>
  );
}

export function SyncSpotlight() {
  const { t } = useTranslation("marketing");
  return (
    <section className="site-section site-sync-spotlight" aria-labelledby="sync-spotlight-title">
      <div>
        <p className="site-eyebrow">
          {t("syncNav")} · {t(DRIVE_SYNC_RELEASED ? "syncStatusAvailable" : "syncStatus")}
        </p>
        <h2 id="sync-spotlight-title">{t("syncSpotlightTitle")}</h2>
        <p>{t(DRIVE_SYNC_RELEASED ? "syncSpotlightDescription" : "syncSpotlightPreview")}</p>
        <a className="site-text-link" href="/sync">
          {t("syncGuideLink")} <span aria-hidden="true">↗</span>
        </a>
      </div>
      <SyncDiagram />
    </section>
  );
}

type GuideStage = "device" | "google" | "confirm" | "qr" | "approve" | "receive";

const guideImages = {
  "zh-HK": {
    device: [zhDevice, 736, 537],
    google: [zhGoogle, 736, 551],
    confirm: [zhConfirm, 736, 752],
    qr: [zhQr, 655, 728],
    approve: [zhApprove, 655, 349],
    receive: [zhReceive, 662, 403],
  },
  en: {
    device: [enDevice, 736, 562],
    google: [enGoogle, 736, 551],
    confirm: [enConfirm, 736, 758],
    qr: [enQr, 655, 728],
    approve: [enApprove, 655, 357],
    receive: [enReceive, 662, 403],
  },
} as const;

function GuideImage({ stage, title }: { stage: GuideStage; title: string }) {
  const { t, i18n } = useTranslation("marketing");
  const language = i18n.resolvedLanguage === "en" ? "en" : "zh-HK";
  const [src, width, height] = guideImages[language][stage];
  return (
    <a
      className="site-sync-guide-image"
      href={src}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t("syncScreenshotOpen", { title })}
    >
      <img src={src} width={width} height={height} alt={t("syncScreenshotAlt", { title })} loading="lazy" />
      <span>
        {t("syncScreenshotEnlarge")} <span aria-hidden="true">↗</span>
      </span>
    </a>
  );
}

function GuideStep({
  number,
  title,
  children,
  stage,
  note,
}: {
  number: string;
  title: string;
  children: ReactNode;
  stage: GuideStage;
  note?: string;
}) {
  return (
    <li>
      <GuideImage stage={stage} title={title} />
      <div className="site-sync-step-copy">
        <span className="site-sync-step-index" aria-hidden="true">
          {number}
        </span>
        <h3>{title}</h3>
        <p>{children}</p>
        {note && <p className="site-sync-step-note">{note}</p>}
      </div>
    </li>
  );
}

export default function SyncGuide() {
  const { t } = useTranslation("marketing");
  const { t: sync } = useTranslation("syncReview");
  return (
    <>
      <nav className="site-section site-sync-jumps" aria-label={t("syncGuideNavigation")}>
        <a href="#first-device">01 {t("syncFirstTitle")}</a>
        <a href="#another-device">02 {t("syncPairTitle")}</a>
        <a href="#daily-sync">03 {t("syncDailyTitle")}</a>
      </nav>
      {!DRIVE_SYNC_RELEASED && <p className="site-section site-sync-preview-note">{t("syncGuidePreviewNotice")}</p>}
      <section className="site-section site-sync-overview" aria-labelledby="sync-overview-title">
        <div>
          <p className="site-eyebrow">{t("syncOverviewKicker")}</p>
          <h2 id="sync-overview-title">{t("syncOverviewTitle")}</h2>
          <p>{t("syncOverviewDescription")}</p>
        </div>
        <SyncDiagram />
      </section>
      <section className="site-section site-sync-tutorial" id="first-device" aria-labelledby="sync-first-title">
        <p className="site-eyebrow">01 · {t("syncComputer")}</p>
        <h2 id="sync-first-title">{t("syncFirstTitle")}</h2>
        <p className="site-sync-section-lead">{t("syncFirstLead")}</p>
        <ol className="site-sync-steps">
          <GuideStep number="1" stage="device" title={t("syncFirstStep1Title")}>
            {t("syncFirstStep1")}
          </GuideStep>
          <GuideStep number="2" stage="google" title={t("syncFirstStep2Title")}>
            {t("syncFirstStep2")}
          </GuideStep>
          <GuideStep number="3" stage="confirm" title={t("syncFirstStep3Title")} note={sync("driveStorageHint")}>
            {t("syncFirstStep3")}
          </GuideStep>
        </ol>
        <p className="site-sync-note">{t("syncOptionalNote")}</p>
      </section>
      <section className="site-section site-sync-tutorial" id="another-device" aria-labelledby="sync-pair-title">
        <p className="site-eyebrow">02 · {t("syncTablet")}</p>
        <h2 id="sync-pair-title">{t("syncPairTitle")}</h2>
        <p className="site-sync-section-lead">{t("syncPairLead")}</p>
        <ol className="site-sync-steps">
          <GuideStep number="1" stage="qr" title={t("syncPairStep1Title")}>
            {t("syncPairStep1")}
          </GuideStep>
          <GuideStep number="2" stage="approve" title={t("syncPairStep2Title")}>
            {t("syncPairStep2")}
          </GuideStep>
          <GuideStep number="3" stage="receive" title={t("syncPairStep3Title")}>
            {t("syncPairStep3")}
          </GuideStep>
        </ol>
        <p className="site-sync-note">{t("syncQrNote")}</p>
      </section>
      <section className="site-section site-sync-daily" id="daily-sync" aria-labelledby="sync-daily-title">
        <div>
          <p className="site-eyebrow">03 · {t("syncDailyKicker")}</p>
          <h2 id="sync-daily-title">{t("syncDailyTitle")}</h2>
          <p>{t("syncDailyLead")}</p>
        </div>
        <ol>
          {(["syncDailySave", "syncDailyWait", "syncDailyRead"] as const).map((key, index) => (
            <li key={key}>
              <span aria-hidden="true">0{index + 1}</span>
              {t(key)}
            </li>
          ))}
        </ol>
      </section>
      <section className="site-section site-sync-help" aria-labelledby="sync-help-title">
        <h2 id="sync-help-title">{t("syncHelpTitle")}</h2>
        {(
          [
            ["syncHelpLocalTitle", "syncHelpLocal"],
            ["syncHelpConnectionTitle", "syncHelpConnection"],
            ["syncHelpConflictTitle", "syncHelpConflict"],
            ["syncHelpBackupTitle", "syncHelpBackup"],
          ] as const
        ).map(([title, body]) => (
          <details key={title}>
            <summary>{t(title)}</summary>
            <p>{t(body)}</p>
          </details>
        ))}
        <a className="site-text-link" href="/data">
          {t("syncDataLink")} <span aria-hidden="true">↗</span>
        </a>
      </section>
    </>
  );
}
